import { SideDrawer } from '@stern-ui/ui';
import { Close, Menu } from 'mdi-material-ui';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { useFetchUserQuery } from '@/api';
import { MenuItem } from '@/components/Elements/MenuItem';
import { resetState } from '@/components/PatientDataTable/store/dataTableSlice';
import { menuConfigObject, menuConfigItemHome } from '@/config/menuConfig';
import { SIDE_NAVIGATION_MENU } from '@/globalConsts';
import { trackEvent } from '@/lib/analytics';
import { getFilteredRoles } from '@/utils';

export interface DrawerProps {
	className?: string;
	close: () => void;
	config: menuConfigObject[];
	isOpen: boolean;
	open: () => void;
}

export const Drawer = (props: DrawerProps) => {
	const { className, close, config, isOpen, open } = props;

	const dispatch = useDispatch();
	const { data: userData } = useFetchUserQuery();
	const userFeatures = userData?.features;

	const handleClick = () => {
		dispatch(resetState());
		trackEvent('Sidebar menu item clicked');
	};

	const doShowHome = useMemo(() => {
		// Users with 'internal_study_admin' role have access to both HCI and Reporting feature so we show them the Home Menu Item
		if (
			userFeatures &&
			userFeatures.length === 1 &&
			(userFeatures[0] === 'internal_study_admin' || userFeatures[0] === 'manual_screening')
		)
			return true;

		// Users with 'study_progress_metrics' & 'patient_status_metrics' roles only have access to the Reporting feature so we don't render the Home Menu Item
		if (
			userFeatures &&
			userFeatures.length === 2 &&
			userFeatures.includes('study_progress_metrics') &&
			userFeatures.includes('patient_status_metrics')
		)
			return false;
		if (userFeatures && userFeatures.length > 1) return true;
		return false;
	}, [userFeatures]);

	const menuItemList: menuConfigObject[] = useMemo(() => {
		// Filter the menu items based on user roles
		const filteredRoles = getFilteredRoles(userFeatures) as menuConfigObject[];
		// check if we should show the Home Menu Item
		if (doShowHome) {
			filteredRoles.unshift(menuConfigItemHome);
		}
		return filteredRoles;
	}, [userFeatures, doShowHome]);

	const renderMenuItems = () => {
		const menuItems = menuItemList?.map((menuItem, i) => {
			const { icon, link, testId, title } = menuItem;

			return (
				<NavLink
					key={i}
					to={link}
					title={title}
					// eslint-disable-next-line react/no-children-prop
					children={({ isActive }) => {
						return (
							<MenuItem
								isExpanded={isOpen}
								icon={icon.nav}
								iconActive={icon.navActive}
								iconHover={icon.navHover}
								isActive={isActive}
								className='mt-2'
								onClick={!isActive ? handleClick : undefined}
							>
								{title}
							</MenuItem>
						);
					}}
					data-test-id={`${testId}-menu-item`}
				></NavLink>
			);
		});
		return menuItems;
	};

	const handleButtonClick = () => {
		if (isOpen) {
			close();
			trackEvent('Sidebar menu closed');
		} else {
			open();
			trackEvent('Sidebar menu expanded');
		}
	};

	return (
		<SideDrawer
			isExpanded={isOpen}
			containerClass={`bg-primaryBackground h-screen min-h-screen pt-5 overflow-x-hidden z-30 ${isOpen ? `${SIDE_NAVIGATION_MENU.WIDTH.EXPANDED}` : `${SIDE_NAVIGATION_MENU.WIDTH.DEFAULT}`} ${className}`}
			buttonClass='top-[1.25rem] right-[0.75rem]'
			handleClick={handleButtonClick}
			name='Main menu'
			openIcon={<Menu className='text-secondaryTextDark' />}
			closeIcon={<Close className='text-secondaryTextDark' />}
			dataTestId='side-navigation-menu'
		>
			<nav className='mx-2 mt-16'>{!config ? null : renderMenuItems()}</nav>
		</SideDrawer>
	);
};
